import { setCommandScope, clearCommandIdentities } from "./data/command";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError, authenticateMiniApp, json, type AppState } from "../api";

const LOGGED_OUT = "estet-explicit-logout";
const timezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const inviteToken = () =>
  new URLSearchParams(window.location.hash.slice(1)).get("invite");
function wasLoggedOut() {
  try {
    return sessionStorage.getItem(LOGGED_OUT) === "1";
  } catch {
    return false;
  }
}
function rememberLogout(value: boolean) {
  try {
    if (value) sessionStorage.setItem(LOGGED_OUT, "1");
    else sessionStorage.removeItem(LOGGED_OUT);
  } catch {
    /* session-only fallback */
  }
}

export function useSession() {
  const client = useQueryClient();
  const [user, setUser] = useState<AppState | null>(null);
  const [error, setError] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(!wasLoggedOut() || !!inviteToken());
  const [attempt, setAttempt] = useState(0);
  // Reuse bootstrap under React StrictMode. Authentication is never a queryFn.
  const pending = useRef<{
    attempt: number;
    promise: Promise<AppState>;
  } | null>(null);
  useEffect(() => {
    if (wasLoggedOut() && !inviteToken()) {
      setLoading(false);
      return;
    }
    let active = true;
    if (!pending.current || pending.current.attempt !== attempt) {
      pending.current = {
        attempt,
        promise: (async () => {
          const invite = inviteToken();
          if (invite) {
            rememberLogout(false);
            const started = await api<{ code: string }>(
              "auth/start",
              json({ token: invite, timezone: timezone() }),
            );
            setCode(started.code);
            const deadline = Date.now() + 120_000;
            while (Date.now() < deadline) {
              const result = await api<{ status: string; code?: string }>(
                "auth/poll",
                json({ timezone: timezone() }),
              );
              if (result.code) setCode(result.code);
              if (result.status === "ready") {
                const fragment = new URLSearchParams(
                  window.location.hash.slice(1),
                );
                fragment.delete("invite");
                history.replaceState(
                  history.state,
                  "",
                  `${location.pathname}${location.search}${fragment.size ? "#" + fragment : ""}`,
                );
                return api<AppState>("state");
              }
              await new Promise((resolve) => setTimeout(resolve, 2000));
            }
            throw new Error(
              "Время подтверждения истекло. Получите новую ссылку в Telegram-боте.",
            );
          }
          try {
            return await api<AppState>("state");
          } catch (e) {
            if (!(e instanceof ApiError) || e.status !== 401) throw e;
          }
          if (await authenticateMiniApp()) return api<AppState>("state");
          throw new Error(
            "Для входа откройте кабинет из Telegram-бота или получите в нём ссылку для компьютера.",
          );
        })(),
      };
    }
    pending.current.promise
      .then((value) => {
        if (active) {
          setCommandScope(String(value.user_id));
          client.setQueryData(["app-state"], value);
          setUser(value);
          setError("");
          setCode("");
        }
      })
      .catch((e) => {
        if (active)
          setError(e instanceof Error ? e.message : "Не удалось войти");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [attempt, client]);

  useEffect(() => {
    const expired = () => {
      if (user) {
        clearCommandIdentities();
    setUser(null);
        client.clear();
        setError("Сессия завершена. Войдите снова.");
        rememberLogout(true);
      }
    };
    window.addEventListener("estet:session-expired", expired);
    return () => window.removeEventListener("estet:session-expired", expired);
  }, [user, client]);
  const retry = () => {
    rememberLogout(false);
    setLoading(true);
    setCode("");
    setError("");
    setAttempt((a) => a + 1);
  };
  const logout = async () => {
    try {
      await api("logout", json({}));
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 401) throw e;
    }
    rememberLogout(true);
    await client.cancelQueries();
    client.clear();
    clearCommandIdentities();
    setUser(null);
    setError("Вы вышли из кабинета.");
  };
  return { user, error, code, loading, retry, logout };
}
